<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import {
    watch,
    computed,
    ref,
    onBeforeMount,
    onMounted,
    onBeforeUnmount,
} from "vue"
import { useRoute, useRouter } from "vue-router"
import type { ConditionBlock } from "@types"
import {
    DDA_STATUS,
    SOURCES_TYPES,
    TASK_REBUILD_SOURCE_DATA,
} from "@core/constants"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useTaskStore } from "@core/stores/task.store"
import {
    useFetchWrapper,
    useHomeysIframe,
    useKPI,
    useWorkflowEnedisMessage,
} from "@core/composables"
import { useI18n } from "vue-i18n"
import { formatIdSource } from "@core/helpers"
import {
    handleBuildingCondition,
    handleSourceCondition,
    handleUserCondition,
} from "@modules/data/helpers"
import {
    evaluateVisibleConditionsBlocks,
    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
} from "@modules/data/utils/evaluateVisibleConditionsBlocks"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Données du bâtiment",
            export: "Exporter les données",
            select_graph:
                "Veuillez sélectionner un graphique pour afficher les données.",
            add_optimal_graph:
                "Si vous ne savez pas lequel choisir, vous pouvez ajouter les graphiques avec des données disponibles pour votre bâtiment",
            select_optimal_graph:
                "Ajouter les graphiques avec des données disponibles",
            pce_freq_missing:
                'Les données du compteur {idsource} ne sont pas encore disponibles. Cliquer sur le bouton "Recharger les données". pour obtenir les dernières données. | Les données des compteurs {idsource} ne sont pas encore disponibles. Cliquer sur le bouton "Recharger les données". pour obtenir les dernières données.',
            reload: "Recharger les données",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type GlobalMessage = {
    message: string
    severity: string
    closable?: boolean
    action?: { fn: () => void; message: string; severity?: string }
}

// ================================
// PROPS & EMITS
// ================================
defineEmits(["click:source"])

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const { isHomeysIframe } = useHomeysIframe()
const graphStore = useGraphStore()
const buildingStore = useBuildingStore()
const sourceStore = useSourceStore()
const taskStore = useTaskStore()
const authStore = useAuthStore()

const { getWorkflowEnedisMessageList, fetch: fetchWorkflowEnedis } =
    useWorkflowEnedisMessage()

const { fetch: fetchInitialData, isFetching: isFetchingInitialData } =
    useFetchWrapper(async () => {
        if (!graphStore.isGraphListLoaded) {
            await graphStore.fetchGraphList()
        }
        fetchWorkflowEnedis()
    })

// ================================
// COMPUTED
// ================================
/**
 * Liste des graphiques affichage (avec conditions de visibilité).
 */
const getGraphBuildingList = computed(() => {
    return Object.values(graphStore.getGraphBuildingConfig())
        .filter((graph) => {
            if (graph.visible_conditions?.length === 0) return true
            const isVisible = evaluateVisibleConditionsBlocks(
                graph.visible_conditions,
                graph.visible_conditions_combine,
                VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
                (condition: ConditionBlock) => {
                    if (condition.origin === "user") {
                        return handleUserCondition(condition)
                    }
                    if (!buildingStore.building) return false
                    if (condition.origin === "building") {
                        return handleBuildingCondition(
                            buildingStore.building,
                            condition
                        )
                    }
                    return handleSourceCondition(
                        buildingStore.getSources,
                        condition
                    )
                }
            )
            // si un paramétre graph à été passé en query on affiche ceux passé en paramétres
            if (graphStore?.getGraphQuery?.length > 0) {
                return graphStore.getGraphQuery.includes(graph.id) && isVisible
            }
            return isVisible
        })
        .map((graph) => {
            return {
                graph_id: graph.id,
                name: graph.name,
                date_from: graphStore.period[0],
                date_to: graphStore.period[1],
                freq: graphStore.freq,
            }
        })
})

/**
 * Récupère la liste des options de carte de graphiques disponibles
 */
const getCardGraphOptionList = computed(() => {
    return getGraphBuildingList.value.map((graph) => {
        return graph.graph_id
    })
})

/**
 * Récupère la liste des identifiants de graphique à afficher
 */
const getGraphIdList = computed(() => {
    return getGraphBuildingList.value
        .filter((graph) => graphStore.graphs.includes(graph.graph_id))
        .map((graph) => graph.graph_id)
})

/**
 * Indique si l'utilisateur possède le droit de télécharger les exports
 */
const getHasExport = computed(() => {
    return authStore.isClientHomeys
})

/**
 * Indique si les graphiques sont en cours de chargement
 */
const isLoadingGraphs = computed(() => {
    return (
        isFetchingInitialData.value || buildingStore.isFetchingBuildingSources
    )
})

// ================================
// STATE
// ================================
/** Contrôle la visibilité de la dialog d'export des données du bâtiment */
const exportDialogVisible = ref(false)

// ================================
// COMPOSABLES (KPI)
// ================================
const {
    indicators,
    onClickKPI,
    fetchKPIValue,
    loading: loadingKPI,
    error: errorKPI,
} = useKPI(
    "building",
    computed(() => graphStore.getGraphBuildingConfig())
)

// ================================
// INTERACTIONS
// ================================
/**
 * Ajoute les graphiques avec des données disponibles pour le bâtiment
 * @param clean - Si true, efface le stockage local des graphiques avant
 */
function onClickAddOptimalGraphs(clean = false) {
    if (clean) {
        graphStore.clearGraphStorage("building", buildingStore.building?._id)
    }

    graphStore.updateGraphList(graphStore.getOptimalBuildingGraph)
    setTimeout(() => {
        fetchKPIValue()
    }, 500)
}

/**
 * Ouvre la dialog d'export des données du bâtiment.
 */
function onClickExport() {
    exportDialogVisible.value = true
}

/**
 * Retire un graphique de la liste affichée.
 * @param graphId - Identifiant du graphique à retirer
 */
function onClickRemove(graphId: string): void {
    onUpdateGraphList(
        graphStore.graphs.filter((graph: string) => graph !== graphId)
    )
}

/**
 * Met à jour la liste des graphiques et recharge les KPI.
 * @param graphs - Liste des graphiques sélectionnés
 */
function onUpdateGraphList(graphs: string[]) {
    graphStore.updateGraphList(graphs)
    fetchKPIValue()
}

/**
 * Initialise la fréquence à daily au montage si elle vaut raw.
 */
onBeforeMount(() => {
    if (graphStore.freq === "raw") {
        graphStore.freq = "daily"
    }
})

/**
 * Message global affiché en haut de la page (ex. PCE sans fréquence).
 */
const getGlobalMessages = computed<GlobalMessage[]>(() => {
    const messages: GlobalMessage[] = []

    const pceListNotValid = buildingStore.getSources.filter(
        (source) =>
            source.idtype === SOURCES_TYPES.PCE &&
            source?.informations?.pce?.droit_access?.etat ===
                DDA_STATUS.ACTIVE &&
            !source?.informations?.pce?.compteur?.frequence
    )

    if (pceListNotValid?.length > 0) {
        messages.push({
            message: t("pce_freq_missing", {
                idsource: pceListNotValid
                    .map((pce) => formatIdSource(pce.idsource, pce.idtype))
                    .join(", "),
            }),
            severity: "warn",
            closable: true,
            action: {
                fn: () =>
                    onClickReload(pceListNotValid.map((pce) => pce._id_source)),
                message: t("reload"),
                severity: "warn",
            },
        })
    }

    return messages
})

/**
 * Lance la tâche de rechargement des données (Franz) pour chaque source, comme en legacy.
 * @param _id_sources - Liste des identifiants de source à recharger
 */
async function onClickReload(_id_sources: string[]) {
    const id = route.params._id_building
    const idStr = Array.isArray(id) ? id[0] : id
    if (!idStr) return

    for (const _id_source of _id_sources) {
        const source = buildingStore.getSources.find(
            (s) => s._id_source === _id_source
        )
        if (!source) continue

        const kwargs: Record<string, unknown> = { _id_source }
        if (
            [SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
                source.idtype as string
            )
        ) {
            kwargs.rebuild = true
        }

        try {
            await taskStore.launchTask(
                TASK_REBUILD_SOURCE_DATA,
                kwargs,
                undefined,
                {
                    taskname: TASK_REBUILD_SOURCE_DATA,
                    autoDelete: true,
                    onSuccess: async () => {
                        await sourceStore.fetchSourceById({ _id_source })
                        await buildingStore.fetchBuilding(idStr, {
                            silent: true,
                        })
                        await buildingStore.fetchBuildingSources({
                            force: true,
                            silent: true,
                        })
                        fetchWorkflowEnedis()
                    },
                }
            )
        } catch (error) {
            console.error(
                "Erreur lors du lancement de la tâche de rechargement des données",
                error
            )
        }
    }
}

// ================================
// EMBED : navigation pilotée par le parent (ex. widget VCS)
// ================================
/**
 * En iframe widget, le parent envoie `{ type: 'update:building', _id_building }` pour changer
 * de fiche sans recharger l’iframe (voir reload côté parent).
 */
function handleEmbedParentBuildingMessage(event: MessageEvent): void {
    if (!isHomeysIframe.value) return
    if (event.source !== window.parent) return
    if (event.data?.type !== "update:building") return
    const nextId = event.data._id_building
    if (typeof nextId !== "string" || !nextId.trim()) return
    const current = router.currentRoute.value
    void router.replace({
        name: "PageBuildingData",
        params: { _id_building: nextId.trim() },
        query: { ...current.query },
    })
}

onMounted(() => {
    window.addEventListener("message", handleEmbedParentBuildingMessage)
})

// ================================
// WATCHERS
// ================================
/**
 * Surveille les changements de bâtiment et charge les données si nécessaire
 */
watch(
    () => route.params?._id_building,
    () => {
        void fetchInitialData()
    },
    {
        immediate: true,
    }
)

/**
 * Initialise graphStore.graphs quand la config graph + le bâtiment sont prêts
 * et que le chargement des sources est terminé (sinon getOptimalBuildingGraph
 * voit getSources vide : enfant montable avant la fin de fetchBuildingSources
 * dans {@link PageBuildingWrapper}).
 */
watch(
    [
        () => isLoadingGraphs.value,
        () => route.params._id_building,
        () => graphStore.isGraphListLoaded,
        () => buildingStore.building?._id,
    ],
    ([isLoadingGraphs, buildingParam, graphLoaded, buildingId]) => {
        if (isLoadingGraphs) return
        if (!buildingParam || !graphLoaded || !buildingId) return
        graphStore.updateGraphList(graphStore.getOptimalBuildingGraph)
    },
    { immediate: true }
)

onBeforeUnmount(() => {
    window.removeEventListener("message", handleEmbedParentBuildingMessage)
    graphStore.cancelPendingGraphRequests()
})
</script>
<template>
    <div class="page page-building-data">
        <!-- ================================ -->
        <!-- EN-TÊTE -->
        <!-- ================================ -->
        <HeaderPageBuilding
            :title="t('title')"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <BaseButton
                v-if="getHasExport"
                :label="t('export')"
                size="small"
                icon="pi pi-download"
                :disabled="isFetchingInitialData"
                @click="onClickExport"
            />
        </HeaderPageBuilding>

        <!-- ================================ -->
        <!-- ÉTAT DE CHARGEMENT -->
        <!-- ================================ -->
        <template v-if="isLoadingGraphs">
            <BaseSkeleton width="100%" height="3rem" />
            <BaseSkeleton width="100%" height="40rem" />
            <BaseSkeleton width="100%" height="40rem" />
        </template>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template v-else>
            <!-- Messages globaux (ex. PCE sans fréquence) -->
            <div v-if="getGlobalMessages.length > 0">
                <BaseMessage
                    v-for="(message, index) in getGlobalMessages"
                    :key="index"
                    icon="pi pi-info-circle"
                    :severity="message.severity"
                    class="page-building-data__message-empty w-fit mx-auto"
                    :closable="message.closable"
                >
                    <div
                        class="page-building-data__message-empty-content w-fit flex flex-col items-start justify-center gap-6"
                    >
                        <p v-html="message.message"></p>
                        <BaseButton
                            v-if="message.action"
                            class="mx-auto"
                            size="small"
                            :severity="message.action?.severity ?? 'primary'"
                            @click="message.action.fn"
                            :label="message.action.message"
                        />
                    </div>
                </BaseMessage>
            </div>
            <!-- En-tête filtres (fréquence, période, graphiques) -->
            <DataHeader
                :option-graphs="getCardGraphOptionList"
                v-model:freq="graphStore.freq"
                v-model:period="graphStore.period"
                :freqs="['hourly', 'daily', 'weekly', 'monthly']"
                :graphs="graphStore.graphs"
                optimal-button
                @update:graphs="onUpdateGraphList"
                @click:optimal="onClickAddOptimalGraphs"
            ></DataHeader>
            <!-- Indicateurs KPI -->
            <DataIndicatorList
                v-if="indicators.length > 0"
                :loading="loadingKPI"
                :error="errorKPI"
                :indicators="indicators"
                @click:kpi="onClickKPI"
            ></DataIndicatorList>
            <!-- Messages workflow Enedis -->
            <div
                v-if="getWorkflowEnedisMessageList.length > 0"
                class="page-building-data__workflow-enedis flex flex-col gap-4"
            >
                <DataChartMessage
                    v-for="(message, index) in getWorkflowEnedisMessageList"
                    :key="index"
                    class="!w-full"
                    :message="message"
                    :current-building-id="buildingStore.building?._id"
                    :closable="true"
                    severity="info"
                />
            </div>

            <!-- Liste des graphiques -->
            <DataChartListItem
                v-for="graphId in getGraphIdList"
                :key="graphId"
                :graph-id="graphId"
                type="building"
                :building="buildingStore.building"
                :sources="buildingStore.getSources"
                @click:source="$emit('click:source', $event)"
                @click:remove="onClickRemove(graphId)"
            />
            <!-- Message vide : aucun graphique sélectionné -->
            <BaseMessage
                v-if="getGraphIdList.length === 0 && !isLoadingGraphs"
                icon="pi pi-info-circle"
                severity="info"
                class="page-building-data__message-empty w-fit mx-auto"
            >
                <div
                    class="page-building-data__message-empty-content w-fit flex flex-col items-start justify-center gap-6"
                >
                    <p>
                        {{ t("select_graph") }}
                    </p>
                    <p>
                        {{ t("add_optimal_graph") }}
                    </p>
                    <BaseButton
                        class="mx-auto"
                        size="small"
                        @click="onClickAddOptimalGraphs(true)"
                        :label="t('select_optimal_graph')"
                    />
                </div>
            </BaseMessage>
        </template>

        <!-- Dialog d'export des données du bâtiment -->
        <ExportBuildingDialog v-model:visible="exportDialogVisible" />
    </div>
</template>
